import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { authService } from '../services/api';
import UserPointsBadge from './UserPointsBadge';
import NotificationBell from './NotificationBell';
import { Menu, X, HelpCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

interface User {
  id?: number;
  displayName: string;
  email: string;
  profilePicture?: string;
  role?: string;
  points?: number;
}

const Navbar = () => {
  const { t } = useTranslation();
  const [user, setUser] = useState<User | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();

  // Check auth on mount and when localStorage changes
  useEffect(() => {
    const loadUser = async () => {
      const storedUser = localStorage.getItem('user');
      const token = localStorage.getItem('token');

      if (token) {
        // Optimistic load from local storage
        if (storedUser) {
          try {
            setUser(JSON.parse(storedUser));
          } catch (error) {
            console.error('Failed to parse user:', error);
          }
        }

        // Fetch fresh profile data
        try {
          const freshUser = await authService.getProfile();
          setUser(freshUser);
          localStorage.setItem('user', JSON.stringify(freshUser));
        } catch (error) {
          console.error('Failed to refresh user profile:', error);
          // If token is invalid, interceptor handles logout/redirect usually
        }
      } else {
        setUser(null);
      }
    };

    loadUser();

    // Listen for storage changes (for multi-tab support)
    window.addEventListener('storage', loadUser);

    // Custom event for same-tab updates
    window.addEventListener('auth-change', loadUser);

    return () => {
      window.removeEventListener('storage', loadUser);
      window.removeEventListener('auth-change', loadUser);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await authService.logout();
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
      setIsOpen(false);

      // Dispatch custom event for other components
      window.dispatchEvent(new Event('auth-change'));

      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <nav className="bg-white shadow-md relative z-50">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-3">
              <img
                src="/thai_card_exchange_logo.png"
                alt="Thai Card Exchange Logo"
                className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover border-2 border-gray-200"
              />
              <span className="font-bold text-gray-800 text-lg md:text-xl truncate max-w-[200px] md:max-w-none">
                Thai Card Exchange
              </span>
            </Link>
          </div>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-6">
            <Link
              to="/browse-cards"
              className="text-gray-700 hover:text-blue-600 transition"
            >
              {t('nav.browseCards')}
            </Link>

            <Link
              to="/marketplace"
              className="text-gray-700 hover:text-blue-600 transition"
            >
              {t('nav.marketplace')}
            </Link>

            <Link
              to="/coming-soon"
              className="text-gray-700 hover:text-blue-600 transition"
            >
              {t('nav.auctions')}
            </Link>

            <Link
              to="/competition-schedule"
              className="text-gray-700 hover:text-blue-600 transition"
            >
              {t('competitionSchedule.title')}
            </Link>

            {user && (
              <>
                {/* My Account Dropdown */}
                <div className="relative group">
                  <button className="text-gray-700 hover:text-blue-600 transition flex items-center gap-1">
                    <span>{t('nav.myAccount')}</span>
                    <span className="text-xs">▼</span>
                  </button>

                  <div className="absolute left-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <Link
                      to="/support"
                      className="block px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition rounded-t-lg"
                    >
                      {t('nav.contactSupport')}
                    </Link>
                    <Link
                      to="/dashboard"
                      className="block px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition"
                    >
                      {t('nav.dashboard')}
                    </Link>

                    <Link
                      to="/coming-soon"
                      className="block px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition rounded-b-lg"
                    >
                      {t('nav.leaderboard')}
                    </Link>
                  </div>
                </div>

                {/* Admin/Moderator Dropdown */}
                {(user.role === 'admin' || user.role === 'moderator') && (
                  <div className="relative group">
                    <button className="text-gray-700 hover:text-blue-600 transition flex items-center gap-1">
                      <span>⚙️</span>
                      <span>{t('nav.admin')}</span>
                      <span className="text-xs">▼</span>
                    </button>

                    <div className="absolute left-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                      {/* 1. Manage Users */}
                      <Link
                        to="/admin/users"
                        className="block px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition rounded-t-lg"
                      >
                        <div className="flex items-center gap-2">
                          <span>👥</span>
                          <span>{t('nav.manageUsers')}</span>
                        </div>
                      </Link>

                      {/* 2. Price Management */}
                      {user.role === 'admin' && (
                        <Link
                          to="/admin/price-management"
                          className="block px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition"
                        >
                          <div className="flex items-center gap-2">
                            <span>📊</span>
                            <span>{t('nav.priceManagement')}</span>
                          </div>
                        </Link>
                      )}

                      {/* 3. Price Moderation */}
                      <Link
                        to="/admin/price-moderation"
                        className="block px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition"
                      >
                        <div className="flex items-center gap-2">
                          <span>💰</span>
                          <span>{t('nav.priceModeration') || 'Price Moderation'}</span>
                        </div>
                      </Link>

                      {/* 4-7. Admin Tools */}
                      {user.role === 'admin' && (
                        <>
                          <Link
                            to="/admin/import-csv"
                            className="block px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition"
                          >
                            <div className="flex items-center gap-2">
                              <span>📊</span>
                              <span>{t('nav.addCards')}</span>
                            </div>
                          </Link>
                          <Link
                            to="/admin/edit-cards"
                            className="block px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition"
                          >
                            <div className="flex items-center gap-2">
                              <span>✏️</span>
                              <span>{t('nav.editCards')}</span>
                            </div>
                          </Link>
                          <Link
                            to="/admin/upload-images"
                            className="block px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition"
                          >
                            <div className="flex items-center gap-2">
                              <span>📤</span>
                              <span>{t('nav.uploadImages')}</span>
                            </div>
                          </Link>
                          <Link
                            to="/admin/listings"
                            className="block px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition"
                          >
                            <div className="flex items-center gap-2">
                              <span>📋</span>
                              <span>{t('nav.manageListings')}</span>
                            </div>
                          </Link>
                        </>
                      )}

                      {/* 8. Support Tickets */}
                      <Link
                        to="/admin/support"
                        className="block px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition"
                      >
                        <div className="flex items-center gap-2">
                          <span>🎫</span>
                          <span>{t('nav.supportTickets')}</span>
                        </div>
                      </Link>

                      {/* Rest of items */}
                      {user.role === 'admin' && (
                        <>
                          <Link
                            to="/admin/announcements"
                            className="block px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition"
                          >
                            <div className="flex items-center gap-2">
                              <span>📢</span>
                              <span>{t('nav.announcements')}</span>
                            </div>
                          </Link>
                          <Link
                            to="/beta-login"
                            className="block px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition"
                          >
                            <div className="flex items-center gap-2">
                              <span>🧪</span>
                              <span>{t('nav.betaLogin')}</span>
                            </div>
                          </Link>
                        </>
                      )}

                      {/* Shared Admin/Moderator Links */}
                      <Link
                        to="/admin/listing-reports"
                        className="block px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition"
                      >
                        <div className="flex items-center gap-2">
                          <span>🚩</span>
                          <span>{t('admin.listingReports', 'Listing Reports')}</span>
                        </div>
                      </Link>
                      <Link
                        to="/admin/carousel"
                        className="block px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition"
                      >
                        <div className="flex items-center gap-2">
                          <span>🎠</span>
                          <span>{t('carouselManagement.title')}</span>
                        </div>
                      </Link>
                      <Link
                        to="/admin/competition-schedule"
                        className="block px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition rounded-b-lg"
                      >
                        <div className="flex items-center gap-2">
                          <span>🏆</span>
                          <span>{t('competitionSchedule.admin.title')}</span>
                        </div>
                      </Link>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Utility Icons Group */}
            <div className="flex items-center gap-4">
              <Link
                to="/how-to"
                className="text-gray-700 hover:text-blue-600 transition"
                title={t('howTo.title')}
              >
                <HelpCircle size={24} />
              </Link>

              <LanguageSwitcher />

              {user && (
                <NotificationBell onUnreadCountChange={setUnreadCount} />
              )}
            </div>

            {/* Profile Section or Login */}
            {user ? (
              <div className="flex items-center gap-3">
                {user.profilePicture && (
                  <Link to={`/users/${user.id}/reviews`} className="block">
                    <img
                      src={user.profilePicture}
                      alt={user.displayName}
                      className="w-8 h-8 rounded-full border-2 border-gray-200 hover:border-blue-500 transition"
                    />
                  </Link>
                )}
                {user.points !== undefined && (
                  <Link to="/coming-soon" className="hover:opacity-80 transition">
                    <UserPointsBadge points={user.points} />
                  </Link>
                )}
                <span className="text-gray-700 font-medium">{user.displayName}</span>
                <button
                  onClick={handleLogout}
                  className="text-gray-700 hover:text-red-600 transition"
                >
                  {t('nav.signOut')}
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
              >
                {t('nav.signIn')}
              </Link>
            )}
          </div>

          {/* Mobile Menu Button - REFACTORED */}
          <div className="lg:hidden flex items-center gap-3">
            {/* ONLY Browse Cards Link outside */}
            <Link
              to="/browse-cards"
              className="text-gray-700 hover:text-blue-600 transition font-medium text-sm"
            >
              {t('nav.browseCards')}
            </Link>

            <Link
              to="/how-to"
              className="text-gray-700 hover:text-blue-600 transition font-medium text-sm"
              title={t('howTo.title')}
            >
              <HelpCircle size={24} />
            </Link>

            {/* User Profile Icon for Mobile */}
            {user && user.profilePicture && (
              <Link to={`/users/${user.id}/reviews`} className="block">
                <img
                  src={user.profilePicture}
                  alt={user.displayName}
                  className="w-8 h-8 rounded-full border-2 border-gray-200"
                />
              </Link>
            )}

            {/* Mobile Notification Bell (Hidden here, shown in menu, but we need state for red dot on menu icon) */}
            {/* Actually user wants red dot ON the hamburger icon */}

            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-700 hover:text-blue-600 focus:outline-none relative"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
              {user && unreadCount > 0 && !isOpen && (
                <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center border border-white">
                  {/* Dot */}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {isOpen && (
          <div className="lg:hidden py-4 border-t border-gray-100">
            <div className="flex flex-col gap-4">
              <div className="px-2 flex justify-end">
                <LanguageSwitcher />
              </div>

              {/* Mobile: User Info now INSIDE hamburger menu */}
              {user ? (
                <div className="bg-gray-50 rounded-lg p-4 mb-4 mx-2">
                  <div className="flex items-center gap-3 mb-3">
                    {user.profilePicture && (
                      <Link to={`/users/${user.id}/reviews`} className="block" onClick={() => { setIsOpen(false); window.scrollTo(0, 0); }}>
                        <img
                          src={user.profilePicture}
                          alt={user.displayName}
                          className="w-10 h-10 rounded-full border-2 border-gray-200"
                        />
                      </Link>
                    )}
                    <div>
                      <div className="font-semibold">{user.displayName}</div>
                      {user.points !== undefined && (
                        <Link to="/coming-soon" className="block mt-1" onClick={() => { setIsOpen(false); window.scrollTo(0, 0); }}>
                          <UserPointsBadge points={user.points} />
                        </Link>
                      )}
                    </div>
                  </div>

                  {/* Mobile Notification Bell moved here */}
                  <div className="border-t border-gray-200 pt-3 mt-2 flex justify-between items-center">
                    <span className="text-sm text-gray-600">{t('nav.notifications')}</span>
                    <NotificationBell />
                  </div>
                </div>
              ) : (
                // Mobile: Sign In moved inside hamburger menu
                <Link
                  to="/login"
                  className="mx-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition text-center"
                  onClick={() => { setIsOpen(false); window.scrollTo(0, 0); }}
                >
                  {t('nav.signIn')}
                </Link>
              )}

              {/* Navigation Links */}
              <Link
                to="/browse-cards"
                className="text-gray-700 hover:text-blue-600 transition px-4 py-2 hover:bg-gray-50 rounded-lg"
                onClick={() => { setIsOpen(false); window.scrollTo(0, 0); }}
              >
                {t('nav.browseCards')}
              </Link>

              <Link
                to="/how-to"
                className="text-gray-700 hover:text-blue-600 transition px-4 py-2 hover:bg-gray-50 rounded-lg"
                onClick={() => { setIsOpen(false); window.scrollTo(0, 0); }}
              >
                {t('howTo.title')}
              </Link>

              <Link
                to="/marketplace"
                className="text-gray-700 hover:text-blue-600 transition px-4 py-2 hover:bg-gray-50 rounded-lg"
                onClick={() => { setIsOpen(false); window.scrollTo(0, 0); }}
              >
                {t('nav.marketplace')}
              </Link>

              <Link
                to="/coming-soon"
                className="text-gray-700 hover:text-blue-600 transition px-4 py-2 hover:bg-gray-50 rounded-lg"
                onClick={() => { setIsOpen(false); window.scrollTo(0, 0); }}
              >
                {t('nav.auctions')}
              </Link>

              <Link
                to="/competition-schedule"
                className="text-gray-700 hover:text-blue-600 transition px-4 py-2 hover:bg-gray-50 rounded-lg"
                onClick={() => { setIsOpen(false); window.scrollTo(0, 0); }}
              >
                {t('competitionSchedule.title')}
              </Link>

              {user && (
                <>
                  <div className="border-t border-gray-100 pt-2 mt-2">
                    <div className="px-4 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('nav.myAccount')}</div>
                    <Link
                      to="/support"
                      className="block px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition rounded-lg"
                      onClick={() => { setIsOpen(false); window.scrollTo(0, 0); }}
                    >
                      {t('nav.contactSupport')}
                    </Link>
                    <Link
                      to="/dashboard"
                      className="block px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition rounded-lg"
                      onClick={() => { setIsOpen(false); window.scrollTo(0, 0); }}
                    >
                      {t('nav.dashboard')}
                    </Link>
                    <Link
                      to="/coming-soon"
                      className="block px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition rounded-lg"
                      onClick={() => { setIsOpen(false); window.scrollTo(0, 0); }}
                    >
                      {t('nav.leaderboard')}
                    </Link>
                  </div>

                  {/* Admin/Moderator Menu */}
                  {(user.role === 'admin' || user.role === 'moderator') && (
                    <div className="border-t border-gray-100 pt-2 mt-2">
                      <div className="px-4 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('nav.admin')}</div>

                      {/* 1. Manage Users */}
                      <Link
                        to="/admin/users"
                        className="block px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition rounded-lg"
                        onClick={() => { setIsOpen(false); window.scrollTo(0, 0); }}
                      >
                        {t('nav.manageUsers')}
                      </Link>

                      {/* 2. Price Management */}
                      {user.role === 'admin' && (
                        <Link
                          to="/admin/price-management"
                          className="block px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition rounded-lg"
                          onClick={() => { setIsOpen(false); window.scrollTo(0, 0); }}
                        >
                          {t('nav.priceManagement')}
                        </Link>
                      )}

                      {/* 3. Price Moderation */}
                      <Link
                        to="/admin/price-moderation"
                        className="block px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition rounded-lg"
                        onClick={() => { setIsOpen(false); window.scrollTo(0, 0); }}
                      >
                        {t('nav.priceModeration') || 'Price Moderation'}
                      </Link>

                      {/* 4-7. Admin Tools */}
                      {user.role === 'admin' && (
                        <>
                          <Link
                            to="/admin/import-csv"
                            className="block px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition rounded-lg"
                            onClick={() => { setIsOpen(false); window.scrollTo(0, 0); }}
                          >
                            {t('nav.addCards')}
                          </Link>
                          <Link
                            to="/admin/edit-cards"
                            className="block px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition rounded-lg"
                            onClick={() => { setIsOpen(false); window.scrollTo(0, 0); }}
                          >
                            {t('nav.editCards')}
                          </Link>
                          <Link
                            to="/admin/upload-images"
                            className="block px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition rounded-lg"
                            onClick={() => { setIsOpen(false); window.scrollTo(0, 0); }}
                          >
                            {t('nav.uploadImages')}
                          </Link>
                          <Link
                            to="/admin/listings"
                            className="block px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition rounded-lg"
                            onClick={() => { setIsOpen(false); window.scrollTo(0, 0); }}
                          >
                            {t('nav.manageListings')}
                          </Link>
                        </>
                      )}

                      {/* 8. Support Tickets */}
                      <Link
                        to="/admin/support"
                        className="block px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition rounded-lg"
                        onClick={() => { setIsOpen(false); window.scrollTo(0, 0); }}
                      >
                        {t('nav.supportTickets')}
                      </Link>

                      {/* Rest of items */}
                      {user.role === 'admin' && (
                        <>
                          <Link
                            to="/admin/announcements"
                            className="block px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition rounded-lg"
                            onClick={() => { setIsOpen(false); window.scrollTo(0, 0); }}
                          >
                            {t('nav.announcements')}
                          </Link>
                          <Link
                            to="/beta-login"
                            className="block px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition rounded-lg"
                            onClick={() => { setIsOpen(false); window.scrollTo(0, 0); }}
                          >
                            {t('nav.betaLogin')}
                          </Link>
                        </>
                      )}

                      {/* Shared Admin/Moderator Links */}
                      <Link
                        to="/admin/listing-reports"
                        className="block px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition rounded-lg"
                        onClick={() => { setIsOpen(false); window.scrollTo(0, 0); }}
                      >
                        <div className="flex items-center gap-2">
                          <span>🚩</span>
                          <span>{t('admin.listingReports', 'Listing Reports')}</span>
                        </div>
                      </Link>
                      <Link
                        to="/admin/carousel"
                        className="block px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition rounded-lg"
                        onClick={() => { setIsOpen(false); window.scrollTo(0, 0); }}
                      >
                        {t('carouselManagement.title')}
                      </Link>
                      <Link
                        to="/admin/competition-schedule"
                        className="block px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition rounded-lg"
                        onClick={() => { setIsOpen(false); window.scrollTo(0, 0); }}
                      >
                        {t('competitionSchedule.admin.title')}
                      </Link>
                    </div>
                  )}

                  <div className="border-t border-gray-100 pt-4 mt-2 px-2">
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-red-600 hover:bg-red-50 hover:text-red-700 font-medium transition rounded-lg"
                    >
                      {t('nav.signOut')}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav >
  );
};

export default Navbar;
